// Web Phone — in-browser WebRTC softphone (v1). 3CX web-client dialer parity:
// register your own extension over SIP-WSS and place/answer calls with mic
// audio, no desk phone or app needed. All SIP mechanics live in
// lib/webphone.ts; this page is a renderer over its snapshots.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Phone, PhoneCall, PhoneOff, PhoneIncoming, Mic, MicOff, Delete, RefreshCw,
  PhoneForwarded, CheckCircle, XCircle, Loader,
} from 'lucide-react';
import { getToken } from '../lib/api';
import { Webphone, WebphoneSnapshot, WebphoneConfigResponse } from '../lib/webphone';

const fJson = async (url: string) => {
  const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!r.ok) {
    let msg = `${r.status}`;
    try { msg = (await r.json()).error || msg; } catch { /* keep status */ }
    throw new Error(msg);
  }
  return r.json();
};

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'] as const;

const REG_CHIP: Record<WebphoneSnapshot['reg'], { label: string; cls: string }> = {
  idle:         { label: 'Offline',      cls: 'bg-elevated text-textSecondary' },
  connecting:   { label: 'Connecting…',  cls: 'bg-warning/15 text-warning' },
  registered:   { label: 'Registered',   cls: 'bg-success/15 text-success' },
  unregistered: { label: 'Unregistered', cls: 'bg-elevated text-textSecondary' },
  failed:       { label: 'Failed',       cls: 'bg-danger/15 text-danger' },
};

function fmtDuration(fromMs: number, nowMs: number) {
  const s = Math.max(0, Math.floor((nowMs - fromMs) / 1000));
  const m = Math.floor(s / 60), r = s % 60;
  const h = Math.floor(m / 60), mr = m % 60;
  return h > 0
    ? `${h}:${String(mr).padStart(2, '0')}:${String(r).padStart(2, '0')}`
    : `${m}:${String(r).padStart(2, '0')}`;
}

export default function WebPhone() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [snap, setSnap] = useState<WebphoneSnapshot | null>(null);
  const phone = useMemo(() => new Webphone(setSnap), []);

  const [cfg, setCfg] = useState<WebphoneConfigResponse | null>(null);
  const [cfgError, setCfgError] = useState('');
  const [loading, setLoading] = useState(true);
  const [number, setNumber] = useState('');
  const [now, setNow] = useState(Date.now());

  // ── attended (warm) transfer state ──────────────────────────────────────────
  // idle       → user not in a transfer flow
  // entering   → user clicked "Transfer"; showing the extension input
  // consulting → initiate called; consult leg ringing/parked; Complete|Cancel shown
  // completing → complete API in-flight
  // cancelling → cancel API in-flight
  type XferPhase = 'idle' | 'entering' | 'consulting' | 'completing' | 'cancelling';
  const [xferPhase, setXferPhase] = useState<XferPhase>('idle');
  const [xferTarget, setXferTarget] = useState('');
  const [xferCallUuid, setXferCallUuid] = useState('');
  const [xferConsultUuid, setXferConsultUuid] = useState('');
  const [xferError, setXferError] = useState('');

  // Fetch my own extension config, then register.
  const connect = async () => {
    setLoading(true); setCfgError('');
    try {
      const c: WebphoneConfigResponse = await fJson('/api/webphone/config');
      setCfg(c);
      if (audioRef.current) phone.start(c.sip, audioRef.current);
    } catch (e) {
      setCfgError(e instanceof Error ? e.message : 'failed to load webphone config');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    void connect();
    return () => phone.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1 s ticker for the in-call duration readout.
  const inCall = snap?.call === 'in-call';
  useEffect(() => {
    if (!inCall) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [inCall]);

  // Type digits with the keyboard while idle.
  const call = snap?.call ?? 'idle';
  const canDial = !!snap && snap.reg === 'registered' && (call === 'idle' || call === 'ended');

  const press = (k: string) => {
    if (call === 'in-call') phone.sendDtmf(k);
    else setNumber((n) => n + k);
  };

  const dial = () => {
    if (!canDial || !number.trim()) return;
    phone.dial(number);
  };

  // ── attended transfer helpers ───────────────────────────────────────────────
  // POST to a mobile call-control endpoint with the auth token.
  const mobilePost = async (path: string, body: Record<string, string>) => {
    const r = await fetch(path, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return r.json() as Promise<{ ok: boolean; error?: string; consult_uuid?: string; calls?: { uuid: string }[] }>;
  };

  // Step 1: user opens the transfer extension input.
  const openXfer = () => { setXferPhase('entering'); setXferTarget(''); setXferError(''); };

  // Step 1b: user closes the input without dialling.
  const closeXfer = () => { setXferPhase('idle'); setXferError(''); };

  // Step 2: user submits the target extension — hold original, originate consult.
  const initiateXfer = async () => {
    const target = xferTarget.trim();
    if (!target) return;
    setXferPhase('consulting');
    setXferError('');
    try {
      // Resolve the FS channel UUID for the current call via active-calls.
      const r = await fetch('/api/mobile/active-calls', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const { calls } = (await r.json()) as { calls: Array<{ uuid: string }> };
      const myCall = calls?.[0];
      if (!myCall?.uuid) {
        setXferError('Could not find active call — try again');
        setXferPhase('entering');
        return;
      }
      setXferCallUuid(myCall.uuid);
      const res = await mobilePost('/api/mobile/attended-transfer/initiate', {
        call_uuid: myCall.uuid,
        target_extension: target,
      });
      if (!res.ok || !res.consult_uuid) {
        setXferError(res.error ?? 'Transfer initiation failed');
        setXferPhase('entering');
        return;
      }
      setXferConsultUuid(res.consult_uuid);
    } catch {
      setXferError('Network error — PBX unreachable');
      setXferPhase('entering');
    }
  };

  // Step 3a: bridge original caller with the consult party.
  const completeXfer = async () => {
    setXferPhase('completing');
    try {
      const res = await mobilePost('/api/mobile/attended-transfer/complete', {
        call_uuid: xferCallUuid,
        consult_uuid: xferConsultUuid,
      });
      if (res.ok) {
        // Transfer done — reset state; FS will BYE our SIP session shortly.
        setXferPhase('idle');
        setXferCallUuid(''); setXferConsultUuid('');
      } else {
        setXferError(res.error ?? 'Bridge failed');
        setXferPhase('consulting');
      }
    } catch {
      setXferError('Network error');
      setXferPhase('consulting');
    }
  };

  // Step 3b: abort — hang up consult and resume the held original call.
  const cancelXfer = async () => {
    setXferPhase('cancelling');
    try {
      await mobilePost('/api/mobile/attended-transfer/cancel', {
        call_uuid: xferCallUuid,
        consult_uuid: xferConsultUuid,
      });
    } catch { /* best-effort — FS resumes unhold regardless */ }
    setXferPhase('idle');
    setXferCallUuid(''); setXferConsultUuid(''); setXferError('');
  };

  // Reset transfer state whenever the call ends so a stale consult_uuid never lingers.
  const wasInCall = useRef(false);
  useEffect(() => {
    const nowInCall = call === 'in-call';
    if (wasInCall.current && !nowInCall) {
      setXferPhase('idle'); setXferCallUuid(''); setXferConsultUuid(''); setXferError('');
    }
    wasInCall.current = nowInCall;
  }, [call]);

  const reg = snap?.reg ?? 'idle';
  const chip = REG_CHIP[reg];

  return (
    <div className="max-w-md mx-auto space-y-4">
      {/* Remote-party audio sink — must live in the DOM for playback. */}
      <audio ref={audioRef} autoPlay />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <PhoneCall size={20} className="text-primary" /> Web Phone
          </h1>
          {cfg && (
            <p className="text-sm text-textSecondary mt-0.5">
              {cfg.display_name} &middot; ext {cfg.extension}
            </p>
          )}
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${chip.cls}`}>{chip.label}</span>
      </div>

      {loading && <div className="text-sm text-textSecondary">Loading your extension…</div>}

      {!loading && cfgError && (
        <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
          <div className="text-sm text-danger">{cfgError}</div>
          <p className="text-xs text-textSecondary">
            The Web Phone registers the extension linked to your account. If your account has no
            extension, ask your administrator.
          </p>
          <button
            onClick={() => void connect()}
            className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md bg-elevated hover:bg-border"
          ><RefreshCw size={14} /> Retry</button>
        </div>
      )}

      {snap?.error && !cfgError && (
        <div className="text-xs text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2">
          {snap.error}
        </div>
      )}

      {/* Incoming call banner */}
      {call === 'incoming' && snap && (
        <div className="bg-surface border border-primary/50 rounded-lg p-4 flex items-center gap-3 shadow-lg">
          <PhoneIncoming size={20} className="text-primary animate-pulse shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{snap.remoteName || snap.remote}</div>
            <div className="text-xs text-textSecondary truncate">
              Incoming call{snap.remoteName ? ` · ${snap.remote}` : ''}
            </div>
          </div>
          <button
            onClick={() => phone.answer()}
            className="px-3 py-2 rounded-md bg-success text-white text-sm font-medium hover:opacity-90"
          >Answer</button>
          <button
            onClick={() => phone.hangup()}
            className="px-3 py-2 rounded-md bg-danger text-white text-sm font-medium hover:opacity-90"
          >Decline</button>
        </div>
      )}

      {/* Live call card */}
      {(call === 'dialing' || call === 'ringing' || call === 'in-call') && snap && (
        <div className="bg-surface border border-border rounded-lg p-5 text-center space-y-3">
          <div className="text-lg font-semibold truncate">{snap.remoteName || snap.remote}</div>
          <div className="text-sm text-textSecondary">
            {call === 'dialing' && 'Calling…'}
            {call === 'ringing' && 'Ringing…'}
            {call === 'in-call' && snap.answeredAt && fmtDuration(snap.answeredAt, now)}
          </div>

          {call === 'in-call' && (
            <div className="grid grid-cols-3 gap-2 max-w-[240px] mx-auto pt-1">
              {KEYS.map((k) => (
                <button
                  key={k}
                  onClick={() => press(k)}
                  className="py-2.5 rounded-md bg-elevated hover:bg-border text-base font-medium"
                >{k}</button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-center gap-3 pt-1">
            {call === 'in-call' && (
              <button
                onClick={() => phone.toggleMute()}
                title={snap.muted ? 'Unmute' : 'Mute'}
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  snap.muted ? 'bg-warning text-black' : 'bg-elevated hover:bg-border'
                }`}
              >{snap.muted ? <MicOff size={18} /> : <Mic size={18} />}</button>
            )}
            <button
              onClick={() => phone.hangup()}
              title="Hang up"
              className="w-12 h-12 rounded-full bg-danger text-white flex items-center justify-center hover:opacity-90"
            ><PhoneOff size={18} /></button>
          </div>

          {/* ── Attended (warm) transfer ───────────────────────────────────── */}
          {call === 'in-call' && (
            <div className="border-t border-border pt-3 space-y-2">
              {/* idle: show the Transfer button */}
              {xferPhase === 'idle' && (
                <div className="flex justify-center">
                  <button
                    onClick={openXfer}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-elevated hover:bg-border text-sm text-textSecondary"
                    title="Attended Transfer — speak with the target before connecting"
                  >
                    <PhoneForwarded size={14} /> Attended Transfer
                  </button>
                </div>
              )}

              {/* entering: extension input */}
              {xferPhase === 'entering' && (
                <div className="space-y-2">
                  <p className="text-xs text-textSecondary text-center">
                    Enter an extension. The current caller will be held while you consult.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      type="text"
                      value={xferTarget}
                      onChange={(e) => setXferTarget(e.target.value.replace(/[^0-9*#]/g, ''))}
                      onKeyDown={(e) => { if (e.key === 'Enter') void initiateXfer(); if (e.key === 'Escape') closeXfer(); }}
                      placeholder="Target extension"
                      className="flex-1 bg-elevated border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                    <button
                      onClick={() => void initiateXfer()}
                      disabled={!xferTarget.trim()}
                      className="px-3 py-2 rounded-md bg-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-40"
                    >Call</button>
                    <button
                      onClick={closeXfer}
                      className="px-3 py-2 rounded-md bg-elevated hover:bg-border text-sm text-textSecondary"
                    >Back</button>
                  </div>
                  {xferError && (
                    <p className="text-xs text-danger text-center">{xferError}</p>
                  )}
                </div>
              )}

              {/* consulting: consult leg is ringing/parked — show Complete + Cancel */}
              {xferPhase === 'consulting' && (
                <div className="space-y-2">
                  <p className="text-xs text-textSecondary text-center">
                    Consulting ext <span className="font-medium text-primary">{xferTarget}</span>
                    &hellip; Original caller is on hold.
                  </p>
                  {xferError && (
                    <p className="text-xs text-danger text-center">{xferError}</p>
                  )}
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => void completeXfer()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-success text-white text-sm font-medium hover:opacity-90"
                      title="Bridge original caller with the consult party"
                    >
                      <CheckCircle size={14} /> Complete
                    </button>
                    <button
                      onClick={() => void cancelXfer()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-danger/10 text-danger hover:bg-danger/20 text-sm"
                      title="Abort transfer and resume the held call"
                    >
                      <XCircle size={14} /> Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* in-flight spinner states */}
              {(xferPhase === 'completing' || xferPhase === 'cancelling') && (
                <div className="flex items-center justify-center gap-2 text-xs text-textSecondary">
                  <Loader size={13} className="animate-spin" />
                  {xferPhase === 'completing' ? 'Connecting…' : 'Cancelling…'}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {call === 'ended' && (
        <div className="bg-surface border border-border rounded-lg p-4 text-center text-sm text-textSecondary">
          Call ended
        </div>
      )}

      {/* Dialer (idle) */}
      {!cfgError && !loading && (call === 'idle' || call === 'ended') && (
        <div className="bg-surface border border-border rounded-lg p-5 space-y-4">
          <div className="flex items-center gap-2">
            <input
              value={number}
              onChange={(e) => setNumber(e.target.value.replace(/[^0-9A-Da-d#*+]/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter') dial(); }}
              placeholder="Enter a number or extension"
              className="flex-1 bg-elevated border border-border rounded-md px-3 py-2.5 text-lg tracking-wider text-center focus:outline-none focus:border-primary"
            />
            {number && (
              <button
                onClick={() => setNumber((n) => n.slice(0, -1))}
                title="Backspace"
                className="p-2.5 rounded-md bg-elevated hover:bg-border text-textSecondary"
              ><Delete size={18} /></button>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2 max-w-[260px] mx-auto">
            {KEYS.map((k) => (
              <button
                key={k}
                onClick={() => press(k)}
                className="py-3 rounded-md bg-elevated hover:bg-border text-lg font-medium"
              >{k}</button>
            ))}
          </div>

          <button
            onClick={dial}
            disabled={!canDial || !number.trim()}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-md bg-success text-white font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
          ><Phone size={18} /> Call</button>

          {reg !== 'registered' && !loading && (
            <p className="text-xs text-textSecondary text-center">
              You can dial once your extension is registered.
            </p>
          )}
        </div>
      )}

      <p className="text-[11px] text-textSecondary text-center">
        Calls use your microphone — allow access when the browser asks. Audio stays between your
        browser and the PBX (SIP over WSS + SRTP).
      </p>
    </div>
  );
}
